import { useState, useCallback } from 'react';
import { TimePeriod, CustomPeriodMultipliers, CustomPointValues, PERIOD_MULTIPLIERS, BalneotherapyType } from '../types';
import { NgapAct } from '../../../types/ngap';
import { POINT_VALUE_METROPOLE, POINT_VALUE_DROM } from '../../../utils/pricingUtils';
import { useAppContext } from '../../../store';
import { trackSimulatorParamsChanged } from '../../../utils/analyticsEvents';

interface UseSimulatorStateReturn {
  selectedPeriod: TimePeriod;
  selectedActId: string | null;
  isDrom: boolean;
  customMultipliers: CustomPeriodMultipliers;
  customPointValues: CustomPointValues;
  addToCart: (act: NgapAct, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, newQuantity: number) => void;
  updateHomeVisitPercentage: (itemId: string, percentage: number) => void;
  updateBalneotherapy: (itemId: string, balneotherapy: BalneotherapyType) => void;
  clearCart: () => void;
  setSelectedPeriod: (period: TimePeriod) => void;
  setSelectedActId: (actId: string | null) => void;
  setIsDrom: (isDrom: boolean) => void;
  updateCustomMultiplier: (period: TimePeriod, value: number) => void;
  updateCustomPointValue: (zone: 'metropole' | 'drom', value: number) => void;
}

export const useSimulatorState = (): UseSimulatorStateReturn => {
  const { dispatch } = useAppContext();
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('month');
  const [selectedActId, setSelectedActId] = useState<string | null>(null);
  const [isDrom, setIsDrom] = useState(false);
  const [customMultipliers, setCustomMultipliers] = useState<CustomPeriodMultipliers>({
    ...PERIOD_MULTIPLIERS
  });
  const [customPointValues, setCustomPointValues] = useState<CustomPointValues>({
    metropole: POINT_VALUE_METROPOLE,
    drom: POINT_VALUE_DROM
  });

  const addToCart = useCallback((act: NgapAct, quantity: number) => {
    const newItem = {
      id: `${act.ID}-${Date.now()}`,
      act,
      quantity,
      homeVisitPercentage: 0,
      balneotherapy: 'none' as BalneotherapyType
    };

    dispatch({ type: 'ADD_SIMULATOR_CART_ITEM', payload: newItem });
  }, [dispatch]);

  const removeFromCart = useCallback((itemId: string) => {
    dispatch({ type: 'REMOVE_SIMULATOR_CART_ITEM', payload: itemId });
  }, [dispatch]);

  const updateQuantity = useCallback((itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      dispatch({ type: 'REMOVE_SIMULATOR_CART_ITEM', payload: itemId });
      return;
    }
    
    dispatch({ type: 'UPDATE_SIMULATOR_CART_ITEM_QUANTITY', payload: { itemId, newQuantity } });
  }, [dispatch]);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_SIMULATOR_CART' });
  }, [dispatch]);

  const updateCustomMultiplier = useCallback((period: TimePeriod, value: number) => {
    setCustomMultipliers(prev => ({
      ...prev,
      [period]: value
    }));
    trackSimulatorParamsChanged(`multiplier_${period}`);
  }, []);

  const updateCustomPointValue = useCallback((zone: 'metropole' | 'drom', value: number) => {
    setCustomPointValues(prev => ({
      ...prev,
      [zone]: value
    }));
    trackSimulatorParamsChanged(`point_value_${zone}`);
  }, []);

  const updateHomeVisitPercentage = useCallback((itemId: string, percentage: number) => {
    const clampedPercentage = Math.max(0, Math.min(100, percentage));

    dispatch({ type: 'UPDATE_SIMULATOR_CART_ITEM_HOME_VISIT', payload: { itemId, percentage: clampedPercentage } });
    trackSimulatorParamsChanged('home_visit_percentage');
  }, [dispatch]);

  const updateBalneotherapy = useCallback((itemId: string, balneotherapy: BalneotherapyType) => {
    dispatch({ type: 'UPDATE_SIMULATOR_CART_ITEM_BALNEOTHERAPY', payload: { itemId, balneotherapy } });
    trackSimulatorParamsChanged(`balneotherapy_${balneotherapy}`);
  }, [dispatch]);

  return {
    selectedPeriod,
    selectedActId,
    isDrom,
    customMultipliers,
    customPointValues,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateHomeVisitPercentage,
    updateBalneotherapy,
    clearCart,
    setSelectedPeriod,
    setSelectedActId,
    setIsDrom,
    updateCustomMultiplier,
    updateCustomPointValue
  };
};